import React, { useEffect, useRef, useState } from 'react'
import SolverLayout from '../../components/layout/SolverLayout'
import SignInHeader from '../../components/admin/SignInHeader'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store'
import { apiRequest } from '../../utils/api'
import { useLearningStore } from '../../store/useLearningStore'
import useRewardStore from '../../store/rewardStore'
import styles from '../../styles/user/ResultNew.module.scss'

const Result = () => {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const updateUserPartial = useAuthStore((state) => state.updateUserPartial)
  const { coins, gems } = useRewardStore()
  const { resetLevelTestAccumulator } = useLearningStore()
  const levelTestAccTotal = useLearningStore((state) => state.levelTestAccTotal)
  const levelTestAccCorrect = useLearningStore((state) => state.levelTestAccCorrect)

  const [summaryData, setSummaryData] = useState({ preResult: '', nextResult: '' })
  const [recommendedLevel, setRecommendedLevel] = useState(null)
  const [sub, setSub] = useState('')
  const hasFetched = useRef(false)

  const summary = {
    total: levelTestAccTotal,
    correct: levelTestAccCorrect,
    wrong: levelTestAccTotal - levelTestAccCorrect,
  }
  const accuracy = summary.total > 0 ? Math.round((summary.correct / summary.total) * 100) : 0

  const [displayAccuracy, setDisplayAccuracy] = useState(0)
  const [displayCorrect, setDisplayCorrect] = useState(0)
  const [displayWrong, setDisplayWrong] = useState(0)

  useEffect(() => {
    if (summary.total === 0) return
    const DURATION = 1200
    const STEPS = 36
    let step = 0
    const timer = setInterval(() => {
      step++
      const eased = 1 - Math.pow(1 - step / STEPS, 3)
      setDisplayAccuracy(Math.round(accuracy * eased))
      setDisplayCorrect(Math.round(summary.correct * eased))
      setDisplayWrong(Math.round(summary.wrong * eased))
      if (step >= STEPS) clearInterval(timer)
    }, DURATION / STEPS)
    return () => clearInterval(timer)
  }, [levelTestAccTotal])

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
    fetchUserResult(user.LEVEL_ID)
  }, [user.LEVEL_ID])

  // ── 기존 로직 그대로 유지 ─────────────────────────────────────────
  const fetchUserResult = async (LEVEL) => {
    try {
      const res = await apiRequest(`/fetchUserResult`, 'POST', {
        LEVEL: LEVEL,
        ID: user.ID,
      })
      console.log('[Result] fetchUserResult res:', res)
      const data = res?.data ?? res
      console.log('[Result] level:', data?.level, '| levelId:', data?.levelId)
      setSummaryData({
        preResult: data.preResult,
        nextResult: data.nextResult,
      })
      if (data.levelId) updateUserPartial({ LEVEL_ID: data.levelId })
      setRecommendedLevel(data.level)
      setSub(data.sub)
    } catch (err) {
      console.error('[Result] fetchUserResult error:', err)
    }
  }

  const handleNextPage = () => {
    resetLevelTestAccumulator()
    if (user.SUB === 'Y') {
      navigate('/userSignin')
    } else {
      navigate('/subscribe')
    }
  }

  const handleBackToDashboard = () => {
    resetLevelTestAccumulator()
    navigate('/dashboard/individual')
  }

  const topicName =
    summaryData.preResult || summaryData.nextResult || 'Math Learning'
  const displayLevel = recommendedLevel ?? '...'

  return (
    <SolverLayout>
      <SignInHeader />

      {/* Coins / Gems overlay */}
      <div className={styles.rewardOverlay}>
        <div className={styles.rewardChip}>
          <img src="/assets/icons/coin.png" alt="coin" style={{ width: 20, height: 20, objectFit: 'contain' }} />
          <span className={styles.rewardChipValue}>{coins.toLocaleString()}</span>
          <span className={styles.rewardChipPlus}>+</span>
        </div>
        <div className={styles.rewardChip}>
          <img src="/assets/icons/gem.png" alt="gem" style={{ width: 20, height: 20, objectFit: 'contain' }} />
          <span className={styles.rewardChipValue}>{gems}</span>
          <span className={styles.rewardChipPlus}>+</span>
        </div>
      </div>

      <div className={styles.contentWrapper}>
        {/* Title */}
        <div className={styles.titleSection}>
          <img src="/assets/icons/celebration.png" alt="celebration" className={styles.titleEmoji} style={{ width: 40, height: 40, objectFit: 'contain' }} />
          <h1 className={styles.title}>Level Test Complete!</h1>
          <p className={styles.subtitle}>
            Great job! We found the perfect starting level for you.
          </p>
        </div>

        {/* Main card */}
        <div className={styles.mainCard}>
          {/* Left: character */}
          <div className={styles.characterSection}>
            <img
              src="/assets/result/result-character.png"
              alt="Character"
              className={styles.characterImg}
              onError={(e) => { e.currentTarget.src = '/assets/rabbit-blue-monster.png' }}
            />
          </div>

          {/* Center: recommended level badge */}
          <div className={styles.levelSection}>
            <p className={styles.levelLabel}>Your Recommended Level</p>
            <div className={styles.shieldOuter}>
              <img src="/assets/icons/star.png" alt="" className={styles.deco} style={{ top: -14, left: -20, width: 22, height: 22, objectFit: 'contain' }} />
              <img src="/assets/icons/star.png" alt="" className={styles.deco} style={{ top: -8, right: -22, width: 16, height: 16, objectFit: 'contain' }} />
              <img src="/assets/icons/star.png" alt="" className={styles.deco} style={{ top: 30, left: -32, width: 13, height: 13, objectFit: 'contain' }} />
              <img src="/assets/icons/star.png" alt="" className={styles.deco} style={{ top: 28, right: -30, width: 18, height: 18, objectFit: 'contain' }} />
              <img src="/assets/icons/star.png" alt="" className={styles.deco} style={{ bottom: 36, left: -26, width: 13, height: 13, objectFit: 'contain' }} />
              <img src="/assets/icons/star.png" alt="" className={styles.deco} style={{ bottom: 48, right: -18, width: 11, height: 11, objectFit: 'contain' }} />
              {recommendedLevel ? (
                <img
                  src={`/assets/result/shield-${recommendedLevel}.png`}
                  alt={`Level ${displayLevel}`}
                  className={styles.levelShieldImg}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    e.currentTarget.nextElementSibling.style.display = 'flex'
                  }}
                />
              ) : null}
              {/* 이미지 로드 실패 시 CSS 방패 fallback */}
              <div className={styles.shieldBody} style={{ display: recommendedLevel ? 'none' : 'flex' }}>
                <span className={styles.shieldNumber}>{displayLevel}</span>
              </div>
            </div>
            <p className={styles.levelMessage}>
              You're ready to start your learning adventure!
            </p>
          </div>

          {/* Right: test summary */}
          <div className={styles.summarySection}>
            <p className={styles.summaryTitle}>Test Summary</p>

            <div className={styles.summaryRow}>
              <div className={styles.summaryIconCircle} style={{ background: '#eef0ff' }}><img src="/assets/icons/target.png" alt="accuracy" style={{ width: 28, height: 28, objectFit: 'contain' }} /></div>
              <div className={styles.summaryRowInfo}>
                <span className={styles.summaryRowLabel}>Accuracy</span>
                <span className={styles.summaryRowValue} style={{ color: '#4361ee' }}>{displayAccuracy}%</span>
              </div>
              <div className={styles.summaryRowRight}>
                <span className={styles.summaryRowCount}>{displayCorrect} / {summary.total}</span>
                <span className={styles.summaryRowCountLabel}>Correct</span>
              </div>
            </div>

            <div className={styles.summaryRow}>
              <div className={styles.summaryIconCircle} style={{ background: '#e8f8ef' }}><img src="/assets/icons/check.png" alt="correct" style={{ width: 28, height: 28, objectFit: 'contain' }} /></div>
              <div className={styles.summaryRowInfo}>
                <span className={styles.summaryRowLabel}>Correct</span>
                <span className={styles.summaryRowValue} style={{ color: '#2d9e5c' }}>{displayCorrect}</span>
              </div>
              <span className={styles.summaryRowCountLabel}>Questions</span>
            </div>

            <div className={styles.summaryRow}>
              <div className={styles.summaryIconCircle} style={{ background: '#fde8e8' }}><img src="/assets/icons/wrong.png" alt="wrong" style={{ width: 28, height: 28, objectFit: 'contain' }} /></div>
              <div className={styles.summaryRowInfo}>
                <span className={styles.summaryRowLabel}>Incorrect</span>
                <span className={styles.summaryRowValue} style={{ color: '#e53e3e' }}>{displayWrong}</span>
              </div>
              <span className={styles.summaryRowCountLabel}>Questions</span>
            </div>
          </div>
        </div>

        {/* Level Progress Preview — level-specific image */}
        {recommendedLevel && (
          <div className={styles.progressPreview}>
            <p className={styles.progressPreviewTitle}>Level Progress Preview</p>
            <p className={styles.progressPreviewSub}>
              Here's what you'll explore at Level {displayLevel}!
            </p>
            <img
              src={`/assets/result/level-${recommendedLevel}.png`}
              alt={`Level ${displayLevel} Progress Preview`}
              className={styles.progressPreviewImg}
              onError={(e) => { e.currentTarget.parentElement.style.display = 'none' }}
            />
          </div>
        )}

        {/* Buttons */}
        <div className={styles.buttonRow}>
          <button className={styles.outlineBtn} onClick={handleBackToDashboard}>
            Back to Dashboard
          </button>
          <button className={styles.primaryBtn} onClick={handleNextPage}>
            Start Learning at Level {displayLevel} →
          </button>
        </div>
      </div>
    </SolverLayout>
  )
}

export default Result
